import React, { useState } from "react";
import ArticleActionsMenu from "./ArticleActionsMenu";
import LabourDeficitModal from "./LabourDeficitModal";
import { formatDate } from "../../utils/date.utils";
import { GrantsLabelItem, ArticleLabelMap } from "../../services/grantsApi";
import LabelCell from "./labels/LabelCell";
import { getColorConfig } from "./labels/labelColors";

// Helper function to remove "Województwo" prefix from province names
const removeWojewodztwoPrefix = (provinceName: string): string => {
  return provinceName.replace(/^Województwo\s+/i, "").trim();
};

interface KfsListItem {
  id: string;
  title: string;
  publicationDate: string;
  excerpt: string;
  url: string;
  detectedKeywords: string[];
  isReserve: boolean;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  office: {
    district: string;
    name: string;
    provinceName: string;
    locality: string;
  };
}

interface SortConfig {
  column: "publicationDate" | "applicationStartDate" | "applicationEndDate";
  order: "ASC" | "DESC";
}

interface KfsTableProps {
  kfsArticles: KfsListItem[];
  onProvinceClick?: (province: string) => void;
  userRole?: string;
  onReprocess?: (
    articleId: string | number,
    type: "kfs" | "bur"
  ) => Promise<void>;
  onReport?: (articleId: string | number, type: "kfs" | "bur") => void;
  onClose?: (articleId: string | number, type: "kfs" | "bur") => Promise<void>;
  onEditFlags?: (articleId: string | number, type: "kfs" | "bur") => void;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSort?: SortConfig | null;
  officesLabourData?: Record<
    string,
    {
      county: string;
      year: number;
      largeShortageProfessions: string[];
      shortageProfessions: string[];
    }
  >;
  showDebugInfo?: boolean;
  labels?: GrantsLabelItem[];
  articleLabels?: ArticleLabelMap;
  onSetArticleLabel?: (articleId: string, labelId: string | null) => Promise<any>;
}

const KfsTable: React.FC<KfsTableProps> = ({
  kfsArticles,
  onProvinceClick,
  userRole,
  onSortChange,
  currentSort,
  officesLabourData,
  onReprocess,
  onReport,
  onClose,
  onEditFlags,
  showDebugInfo,
  labels = [],
  articleLabels = {},
  onSetArticleLabel,
}) => {
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [labourModalData, setLabourModalData] = useState<{
    isOpen: boolean;
    officeName: string;
    county: string;
    year: number;
    largeShortageProfessions: string[];
    shortageProfessions: string[];
  }>({
    isOpen: false,
    officeName: "",
    county: "",
    year: new Date().getFullYear(),
    largeShortageProfessions: [],
    shortageProfessions: [],
  });

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(`Skopiowano ${label}: ${text}`);
      setTimeout(() => setCopiedText(null), 2500);
    } catch (err) {
      // Fallback dla starszych przeglądarek
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedText(`Skopiowano ${label}: ${text}`);
      setTimeout(() => setCopiedText(null), 2500);
    }
  };

  const handleProvinceClick = (province: string) => {
    if (onProvinceClick) {
      onProvinceClick(province);
    }
  };

  const handleOfficeClick = (article: KfsListItem) => {
    const labourData = officesLabourData?.[article.office.district];
    if (labourData) {
      setLabourModalData({
        isOpen: true,
        officeName: article.office.name,
        county: labourData.county,
        year: labourData.year,
        largeShortageProfessions: labourData.largeShortageProfessions,
        shortageProfessions: labourData.shortageProfessions,
      });
    }
  };

  const closeLabourModal = () => {
    setLabourModalData((prev) => ({ ...prev, isOpen: false }));
  };

  const handleSortClick = (
    column: "publicationDate" | "applicationStartDate" | "applicationEndDate"
  ) => {
    if (!onSortChange) return;

    if (currentSort?.column === column) {
      // Simple toggle between ASC and DESC
      const newOrder = currentSort.order === "ASC" ? "DESC" : "ASC";
      onSortChange(column, newOrder);
    } else {
      // New column, start with ASC
      onSortChange(column, "ASC");
    }
  };

  const getSortIcon = (
    column: "publicationDate" | "applicationStartDate" | "applicationEndDate"
  ) => {
    if (currentSort?.column !== column) {
      return (
        <svg
          className="w-4 h-4 ml-1 text-neutral-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    }

    return currentSort.order === "ASC" ? (
      <svg
        className="w-4 h-4 ml-1 text-primary-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 ml-1 text-primary-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  return (
    <div className="overflow-x-auto relative">
      {/* Toast notification */}
      {copiedText && (
        <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 max-w-sm">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm font-medium">{copiedText}</span>
          </div>
        </div>
      )}

      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            <th
              className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 w-24"
              onClick={() => handleSortClick("publicationDate")}
              title="Kliknij aby sortować według daty publikacji"
            >
              <div className="flex items-center">
                Publ.
                {getSortIcon("publicationDate")}
              </div>
            </th>
            <th
              className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 w-24"
              onClick={() => handleSortClick("applicationStartDate")}
              title="Kliknij aby sortować według daty rozpoczęcia naboru"
            >
              <div className="flex items-center">
                Od
                {getSortIcon("applicationStartDate")}
              </div>
            </th>
            <th
              className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 w-24"
              onClick={() => handleSortClick("applicationEndDate")}
              title="Kliknij aby sortować według daty zakończenia naboru"
            >
              <div className="flex items-center">
                Do
                {getSortIcon("applicationEndDate")}
              </div>
            </th>
            <th className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-20">
              Województwo
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/3">
              Urząd i Barometr
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/3">
              Tytuł
            </th>
            {labels.length > 0 && (
              <th className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-20 md:w-28">
                Etykieta
              </th>
            )}
            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-16">
              Akcje
            </th>
            {(userRole === "admin" || showDebugInfo) && (
              <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                UUID
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-200">
          {kfsArticles.map((article) => {
            const artLabel = articleLabels[article.id];
            const rowColor = artLabel ? getColorConfig(artLabel.color) : null;
            return (
            <tr
              key={article.id}
              className={rowColor ? `${rowColor.rowBg} transition-colors` : "hover:bg-neutral-50 transition-colors"}
            >
              <td className="px-2 py-2 whitespace-nowrap text-sm text-left text-neutral-500">
                {formatDate(article.publicationDate)}
              </td>
              <td className="px-2 py-2 whitespace-nowrap text-sm text-left text-neutral-500">
                {formatDate(article.applicationStartDate)}
              </td>
              <td className="px-2 py-2 whitespace-nowrap text-sm text-left text-neutral-500">
                {formatDate(article.applicationEndDate)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-left">
                <button
                  onClick={() =>
                    handleProvinceClick(article.office.provinceName)
                  }
                  className="text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                  title={`Filtruj według województwa: ${removeWojewodztwoPrefix(article.office.provinceName)}`}
                >
                  {removeWojewodztwoPrefix(article.office.provinceName)}
                </button>
              </td>
              <td className="px-3 py-2 text-sm text-left max-w-xs">
                <div className="flex flex-col">
                  <button
                    onClick={() => handleOfficeClick(article)}
                    className={`text-left hover:underline transition-colors truncate ${
                      officesLabourData?.[article.office.district]
                        ? "text-primary-600 hover:text-primary-800 cursor-pointer font-medium"
                        : "text-neutral-700 cursor-default"
                    }`}
                    title={
                      officesLabourData?.[article.office.district]
                        ? "Kliknij aby zobaczyć zawody z deficytem w tym powiecie"
                        : article.office.name
                    }
                    disabled={!officesLabourData?.[article.office.district]}
                  >
                    {article.office.name}
                  </button>
                  {(userRole === "admin" || showDebugInfo) && (
                    <button
                      onClick={() =>
                        copyToClipboard(article.office.district, "ID urzędu")
                      }
                      className="text-xs text-neutral-500 hover:text-neutral-700 cursor-pointer transition-colors text-left mt-1"
                      title={`Kliknij aby skopiować ID urzędu: ${article.office.district}`}
                    >
                      ID: {article.office.district}
                    </button>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-sm text-left text-neutral-900">
                <div className="flex flex-col">
                  <div className="flex flex-wrap items-center gap-1 mb-1">
                    {article.isReserve && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                        Rezerwa
                      </span>
                    )}
                    {article.detectedKeywords.includes("szkolenie") && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Szkolenia
                      </span>
                    )}
                    {article.detectedKeywords.includes("dofinansowanie") && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Dofinansowanie
                      </span>
                    )}
                  </div>
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-800 hover:underline font-medium transition-colors max-w-md truncate block"
                    title={article.title}
                  >
                    {(() => {
                      const title = article.title;
                      return title.length > 80
                        ? `${title.substring(0, 80)}...`
                        : title;
                    })()}
                  </a>
                </div>
              </td>
              {labels.length > 0 && (
                <td className="px-2 py-2 whitespace-nowrap text-sm">
                  {onSetArticleLabel && (
                    <LabelCell
                      articleId={article.id}
                      labels={labels}
                      currentLabel={artLabel || null}
                      onSetLabel={onSetArticleLabel}
                    />
                  )}
                </td>
              )}
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
                {onReprocess && onReport && (
                  <ArticleActionsMenu
                    articleId={article.id}
                    articleType="kfs"
                    articleTitle={article.title}
                    articleUrl={article.url}
                    isAdmin={userRole === "admin"}
                    onReprocess={onReprocess}
                    onReport={onReport}
                    onClose={onClose}
                    onEditFlags={onEditFlags}
                  />
                )}
              </td>
              {(userRole === "admin" || showDebugInfo) && (
                <td className="px-3 py-2 whitespace-nowrap text-sm text-left text-neutral-700">
                  <button
                    onClick={() => copyToClipboard(article.id, "UUID")}
                    className="font-mono text-xs hover:bg-neutral-100 hover:text-neutral-900 cursor-pointer transition-colors px-1 py-1 rounded"
                    title={`Kliknij aby skopiować UUID: ${article.id}`}
                  >
                    {article.id}
                  </button>
                </td>
              )}
            </tr>
            );
          })}
        </tbody>
      </table>

      <LabourDeficitModal
        isOpen={labourModalData.isOpen}
        onClose={closeLabourModal}
        officeName={labourModalData.officeName}
        county={labourModalData.county}
        year={labourModalData.year}
        largeShortageProfessions={labourModalData.largeShortageProfessions}
        shortageProfessions={labourModalData.shortageProfessions}
      />
    </div>
  );
};

export default KfsTable;
